import React from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  FileText,
  Loader2,
  RefreshCw,
  Route,
  ShieldAlert,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useEmailIngestionAudit } from '@/hooks/useEmailScanning';
import type { EmailIngestionAuditAlert, EmailIngestionAuditMatch } from '@/types/emailScanning';

function formatDate(value?: string | null): string {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function statusBadge(status?: string | null) {
  if (!status) return <Badge variant="secondary">n/a</Badge>;
  if (['completed', 'ingested', 'active', 'resolved'].includes(status)) {
    return <Badge className="bg-emerald-500/10 text-emerald-700 border-emerald-500/20">{status}</Badge>;
  }
  if (['failed', 'error', 'critical'].includes(status)) {
    return <Badge variant="destructive">{status}</Badge>;
  }
  if (['pending', 'ingesting', 'warning', 'needs_review'].includes(status)) {
    return <Badge className="bg-amber-500/10 text-amber-700 border-amber-500/20">{status}</Badge>;
  }
  return <Badge variant="secondary">{status}</Badge>;
}

function alertIcon(alert: EmailIngestionAuditAlert) {
  if (alert.severity === 'critical') return <ShieldAlert className="h-4 w-4 text-destructive" />;
  if (alert.severity === 'warning') return <AlertTriangle className="h-4 w-4 text-amber-500" />;
  return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
}

function routeSummary(match: EmailIngestionAuditMatch): string {
  const pipeline = match.routing?.pipeline || match.matchDiagnostics?.pipeline || 'project_ingestion';
  const intent = match.classification?.intent;
  return [pipeline, intent].filter(Boolean).join(' / ');
}

export const IngestionAuditDashboard: React.FC = () => {
  const { audit, loadingAudit, auditError, refreshAudit } = useEmailIngestionAudit();
  const lastJob = audit?.jobs?.[0];

  return (
    <Card className="border-border bg-card">
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-lg">Ingestion Audit</CardTitle>
            <CardDescription>
              Connection health, route decisions, attachment ingestion, and portfolio reporting status.
            </CardDescription>
          </div>
          <Button size="sm" variant="outline" onClick={() => refreshAudit()} disabled={loadingAudit}>
            {loadingAudit ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {auditError && (
          <div className="rounded-md border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive">
            {auditError}
          </div>
        )}

        {loadingAudit && !audit && (
          <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading audit trail...
          </div>
        )}

        {audit && (
          <>
            <div className="grid gap-3 md:grid-cols-4">
              <div className="rounded-lg border border-border p-3">
                <p className="text-xs text-muted-foreground">Organization</p>
                <p className="mt-1 text-sm font-medium">{audit.organizationId}</p>
              </div>
              <div className="rounded-lg border border-border p-3">
                <p className="text-xs text-muted-foreground">Connections</p>
                <p className="mt-1 text-sm font-medium">{audit.connections.length}</p>
              </div>
              <div className="rounded-lg border border-border p-3">
                <p className="text-xs text-muted-foreground">Latest Scan</p>
                <p className="mt-1 text-sm font-medium">
                  {lastJob ? `${lastJob.stats.totalEmailsFound || 0} emails / ${lastJob.stats.totalMatches || 0} matches` : 'No jobs'}
                </p>
              </div>
              <div className="rounded-lg border border-border p-3">
                <p className="text-xs text-muted-foreground">Open Alerts</p>
                <p className="mt-1 text-sm font-medium">
                  {audit.alerts.filter((alert) => alert.status === 'open').length}
                </p>
              </div>
            </div>

            {audit.alerts.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold">Health Alerts</h4>
                <div className="space-y-2">
                  {audit.alerts.slice(0, 4).map((alert) => (
                    <div key={alert.id} className="flex items-start gap-3 rounded-lg border border-border p-3">
                      {alertIcon(alert)}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-sm font-medium">{alert.title}</p>
                          {statusBadge(alert.status)}
                          {statusBadge(alert.severity)}
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">{alert.message}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <h4 className="text-sm font-semibold">Connected Account Audit</h4>
              <div className="overflow-x-auto rounded-lg border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Account</TableHead>
                      <TableHead>Org Mapping</TableHead>
                      <TableHead>Last Scan</TableHead>
                      <TableHead>Last Result</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {audit.connections.map((connection) => (
                      <TableRow key={connection.id}>
                        <TableCell>
                          <div className="text-sm font-medium">{connection.emailAddress}</div>
                          <div className="text-xs text-muted-foreground">{connection.provider}</div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{connection.organizationId}</div>
                          {connection.legacyOrganizationId && (
                            <div className="text-xs text-amber-600">
                              repaired from {connection.legacyOrganizationId}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{formatDate(connection.lastScanAt)}</TableCell>
                        <TableCell className="text-sm">
                          {connection.stats.lastEmailsFound} emails / {connection.stats.lastMatches} matches
                        </TableCell>
                        <TableCell>{statusBadge(connection.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="text-sm font-semibold">Recent Route Decisions</h4>
              <div className="overflow-x-auto rounded-lg border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Matched To</TableHead>
                      <TableHead>Route</TableHead>
                      <TableHead>Attachments</TableHead>
                      <TableHead>Portfolio</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {audit.matches.slice(0, 10).map((match) => (
                      <TableRow key={match.id}>
                        <TableCell className="min-w-[280px]">
                          <div className="text-sm font-medium">{match.subject}</div>
                          <div className="text-xs text-muted-foreground">
                            {match.from?.address || 'unknown'} · {formatDate(match.receivedAt)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm font-medium">{match.projectName}</div>
                          <div className="text-xs text-muted-foreground">
                            {match.matchSource}
                          </div>
                          {match.matchDiagnostics?.reviewReason && (
                            <div className="mt-1 text-xs text-amber-600">{match.matchDiagnostics.reviewReason}</div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm">
                            <Route className="h-3.5 w-3.5 text-muted-foreground" />
                            {routeSummary(match)}
                          </div>
                          <div className="text-xs text-muted-foreground">{match.routing?.reason || match.matchReason}</div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm">
                            <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                            {match.ingestedDocumentCount}/{match.attachmentCount}
                          </div>
                          {statusBadge(match.ingestionStatus)}
                        </TableCell>
                        <TableCell>
                          {statusBadge(match.portfolioIngestionStatus)}
                          {match.portfolioIngestion?.snapshotId && (
                            <div className="mt-1 text-xs text-muted-foreground">
                              snapshot {match.portfolioIngestion.snapshotId}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {audit.matches.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">
                          No matched emails found in the recent audit window.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
